import {useEffect, useRef} from "react";
import {Box, Button, Card, CardContent, Chip, FormControlLabel, IconButton, Stack, Switch,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery, useTheme} from "@mui/material";
import {DeleteOutline, EditOutlined} from "@mui/icons-material";
import {formatMoney} from "./distributor-display";

function Identity({row, t}) {
    return <Box sx={{minWidth: 160, overflowWrap: "anywhere"}}>
        <Typography variant="caption" color="text.secondary">{row.productCode}</Typography>
        <Typography fontWeight={600}>{row.productName}</Typography>
        <Typography variant="body2" color="text.secondary">{[row.productManufacturer, row.productCountry].filter(Boolean).join(" · ")}</Typography>
        {typeof row.inStock === "boolean" && <Typography variant="caption" display="block" color="text.secondary">
            {t(row.inStock ? "InStock" : "OutOfStock")}</Typography>}
        {row.productCategory && <Chip size="small" variant="outlined" label={t(row.productCategory)} sx={{mt: 1}}/>}
    </Box>;
}

function NumberField({field, row, editor, t, language}) {
    const labels = {price: "Price", quantity: "Stock", discount: "Discount"};
    const editing = editor.editing?.productTenantId === row.productTenantId;
    const money = formatMoney(row.price, row.currency, language);
    if (!editing) return <Box><Typography variant="caption" color="text.secondary" sx={{display: {xs: "block", md: "none"}}}>{t(labels[field])}</Typography>
        <Typography>{field === "price" ? money ?? t("DistributorMetricUnavailable")
            : field === "discount" ? `${row.discount ?? "—"}%` : row.quantity ?? "—"}</Typography></Box>;
    return <TextField label={t(labels[field])} type="number" size="small" fullWidth autoFocus={field === "price"}
        value={editor.draft[field]} disabled={editor.busy} error={!!editor.errors[field]}
        helperText={editor.errors[field] ? t(editor.errors[field]) : field === "price" ? money : " "}
        inputProps={{"aria-label": `${t(labels[field])} ${row.productCode}`, min: 0,
            max: field === "discount" ? 100 : field === "quantity" ? 2147483647 : undefined,
            step: field === "quantity" ? 1 : "any"}}
        onChange={event => editor.setDraft(previous => ({...previous, [field]: event.target.value}))}
        onKeyDown={event => {if (event.key === "Enter") {event.preventDefault(); editor.save();}
            if (event.key === "Escape") editor.cancel();}}/>
}

function Status({row, editor, t}) {
    if (editor.editing?.productTenantId === row.productTenantId) return <FormControlLabel sx={{m: 0}}
        control={<Switch size="small" checked={editor.draft.active} disabled={editor.busy}
            inputProps={{"aria-label": `${t("Status")} ${row.productCode}`}}
            onChange={event => editor.setDraft(previous => ({...previous, active: event.target.checked}))}/>}
        label={t(editor.draft.active ? "Active" : "Inactive")}/>;
    return <Chip size="small" variant="outlined" color={row.active ? "success" : "default"}
        label={t(typeof row.active !== "boolean" ? "DistributorMetricUnavailable" : row.active ? "Active" : "Inactive")}/>;
}

function Actions({row, editor, onRemove, t}) {
    const editing = editor.editing?.productTenantId === row.productTenantId;
    const editButton = useRef(null);
    const wasEditing = useRef(false);
    useEffect(() => {
        if (wasEditing.current && !editing) editButton.current?.focus();
        wasEditing.current = editing;
    }, [editing]);
    if (editing) return <Stack direction="row" flexWrap="wrap" gap={1}>
        <Button size="small" variant="contained" disabled={editor.busy} onClick={editor.save}>
            {t(editor.busy ? "ProductsSaving" : editor.notice?.severity === "error" ? "ProductsRetrySave" : "SaveChanges")}</Button>
        <Button size="small" disabled={editor.busy} onClick={editor.cancel}>{t("Cancel")}</Button>
    </Stack>;
    return <Stack direction="row" gap={1}>
        <Button ref={editButton} size="small" startIcon={<EditOutlined/>} disabled={!!editor.editing || editor.busy}
            aria-label={`${t("Edit")} ${row.productCode}`} onClick={() => editor.edit(row)}>{t("Edit")}</Button>
        <Tooltip title={t("Remove")}><span><IconButton size="small" disabled={!!editor.editing || editor.busy}
            aria-label={`${t("Remove")} ${row.productCode}`} onClick={() => onRemove(row)}><DeleteOutline/></IconButton></span></Tooltip>
    </Stack>;
}

export default function ProductCatalogueRows({rows, editor, onRemove, t, language}) {
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down("md"));
    const props = row => ({row, editor, onRemove, t, language});
    if (mobile) return <Stack gap={2}>{rows.map(row => <Card key={row.productTenantId} variant="outlined"><CardContent>
        <Identity row={row} t={t}/>
        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "repeat(3, 1fr)"}, gap: 2, my: 2}}>
            {["price", "quantity", "discount"].map(field => <NumberField key={field} field={field} {...props(row)}/>)}</Box>
        <Typography variant="body2" sx={{mb: 1}}>{t("ExpirationDate")}: {row.endDate || "—"}</Typography>
        <Box sx={{display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", justifyContent: "space-between"}}>
            <Status {...props(row)}/><Actions {...props(row)}/></Box>
    </CardContent></Card>)}</Stack>;
    return <TableContainer tabIndex={0} role="region" aria-label={t("WorkspaceProductsTable")}
        sx={{border: 1, borderColor: "divider", borderRadius: 1, overflowX: "auto"}}>
        <Table aria-label={t("Products")} sx={{minWidth: 1000, tableLayout: "fixed"}}><TableHead><TableRow>
            {["ProductName", "ExpirationDate", "Price", "Stock", "Discount", "Status", "Actions"].map((key, index) =>
                <TableCell key={key} sx={{width: index === 0 ? "25%" : index === 6 ? "20%" : "11%"}}>{t(key)}</TableCell>)}
        </TableRow></TableHead><TableBody>{rows.map(row => <TableRow key={row.productTenantId} hover>
            <TableCell><Identity row={row} t={t}/></TableCell><TableCell>{row.endDate || "—"}</TableCell>
            {["price", "quantity", "discount"].map(field => <TableCell key={field}><NumberField field={field} {...props(row)}/></TableCell>)}
            <TableCell><Status {...props(row)}/></TableCell><TableCell><Actions {...props(row)}/></TableCell>
        </TableRow>)}</TableBody></Table>
    </TableContainer>;
}
